import { ReactElement } from "react";
import { localize } from "client_legacy/ndaw/culture";
import { FormattedText, Typography } from "client/Components";
import { UnreachableCaseError } from "client_legacy/ndaw/errors";
import { UserRequestKind, UserRequestStatus } from "client/Api/UserRequests/userRequest";
import { setTrackingId } from "client/Misc/utils";
import {
	UserRequestNotificationDataViewModel,
	UserRequestNotificationKind,
	UserRequestRequestedNotificationDataViewModel,
	UserRequestRespondedNotificationDataViewModel
} from "client/Api/Notifications/notifications";

export type UserRequestActivityProps = {
	data: UserRequestNotificationDataViewModel;
};

export const UserRequestActivity = (props: UserRequestActivityProps): ReactElement => {
	const { data } = props;

	switch (data.kind) {
		case UserRequestNotificationKind.Requested:
			return <UserRequestRequestedActivity data={data} />;
		case UserRequestNotificationKind.Responded:
			return <UserRequestRespondedActivity data={data} />;
		default:
			throw new UnreachableCaseError(data);
	}
};

UserRequestActivity.displayName = "UserRequestActivity";

type UserRequestRequestedActivityProps = {
	data: UserRequestRequestedNotificationDataViewModel;
};

const UserRequestRequestedActivity = (props: UserRequestRequestedActivityProps): ReactElement => {
	const { data } = props;

	const { item, requesterName } = data;

	const renderText = (): ReactElement => {
		switch (item.kind) {
			case UserRequestKind.AppAccess:
				return (
					<span {...setTrackingId("title")}>
						<FormattedText
							text={localize("_UsernameRequestedAccessToTheAppNameApp")}
							thingsToReplace={[
								{
									what: "{{Username}}",
									filler: (
										<Typography
											trackingId="user-name"
											weight="bold"
										>
											{requesterName}
										</Typography>
									)
								},
								{
									what: "{{AppName}}",
									filler: (
										<Typography
											trackingId="app-name"
											weight="bold"
										>
											{item.name}
										</Typography>
									)
								},
							]}
						/>
					</span>
				);
			default:
				throw new UnreachableCaseError(item.kind);
		}
	};

	return (
		<>
			{renderText()}
		</>
	);
};
UserRequestRequestedActivity.displayName = "UserRequestRequestedActivity";

type UserRequestRespondedActivityProps = {
	data: UserRequestRespondedNotificationDataViewModel;
};

const UserRequestRespondedActivity = (props: UserRequestRespondedActivityProps): ReactElement => {
	const { data } = props;

	const { item } = data;

	const renderText = (): ReactElement => {
		switch (item.kind) {
			case UserRequestKind.AppAccess:
				return (
					<span {...setTrackingId("title")}>
						<FormattedText
							text={localize(statusToKeyMap[data.status])}
							thingsToReplace={[
								{
									what: "{{AppName}}",
									filler: (
										<Typography trackingId="app-name" weight="bold">
											{item.name}
										</Typography>
									)
								}
							]}
						/>
					</span>
				);
			default:
				throw new UnreachableCaseError(item.kind);
		}
	};

	return (
		<>
			{renderText()}
		</>
	);
};
UserRequestRespondedActivity.displayName = "UserRequestRespondedActivity";

const statusToKeyMap: Record<UserRequestStatus.Accepted | UserRequestStatus.Declined, LocalizationKey> = {
	[UserRequestStatus.Accepted]: "_RequestForTheAppNameAppIsAccepted",
	[UserRequestStatus.Declined]: "_RequestForTheAppNameAppIsDeclined"
};
